import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { INVENTORY } from '../../routes/RouteConstant';

import { INVENTORY_TYPES, USER_NAMES, SUPPLIER_LIST } from '../../api/ApiEndpoints';
import { notify } from '../../common/Toast';
import { fetchDashboardSummary, fetchDashboardTables } from '../../redux/action/dashboardAction/DashboardAction';

import PendingApprovalTable from './dashboardPageComponents/PendingApprovalTable';
import DelayedOrdersTable from './dashboardPageComponents/DelayedOrdersTable';
import api from '../../interceptor/Interceptor';
import {
    PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    BarChart, Bar,
} from 'recharts';
import {
    LuLayoutDashboard, LuClipboardList, LuBoxes, LuClock, LuTriangleAlert,
    LuTruck, LuUsers, LuBuilding2, LuPackage,
    LuHourglass, LuCircleDollarSign, LuWallet, LuChevronRight,
} from 'react-icons/lu';
import { CHART_PALETTE, STATUS_COLORS } from '../../utils/Constant';
import StatCard from './dashboardPageComponents/StatCard';
import ChartCard from './dashboardPageComponents/ChartCard';
import ChipCloud from './dashboardPageComponents/ChipCloud';
import PeriodToggle from './dashboardPageComponents/PeriodToggle';
import { money, monthLabel } from '../../utils/helpers';

export default function Dashboard() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector((state) => state.auth.user);
    const { summary, pendingApproval, delayedOrders, summaryLoading, tablesLoading } = useSelector(
        (state) => state.dashboard,
    );

    const [inventoryTypes, setInventoryTypes] = useState([]);
    const [userNames, setUserNames] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [trendPeriod, setTrendPeriod] = useState('month');
    const [expenditurePeriod, setExpenditurePeriod] = useState('month');

    useEffect(() => {
        dispatch(fetchDashboardSummary()).then((res) => {
            if (!res.success) notify.error('Failed to fetch dashboard summary', res.error);
        });
        dispatch(fetchDashboardTables()).then((res) => {
            if (!res.success) notify.error('Failed to fetch dashboard tables', res.error);
        });

        api.get(INVENTORY_TYPES)
            .then((res) => setInventoryTypes(res.data.types || []))
            .catch(() => setInventoryTypes([]));
        api.get(USER_NAMES)
            .then((res) => setUserNames(res.data.users || []))
            .catch(() => setUserNames([]));
        api.get(SUPPLIER_LIST)
            .then((res) => setSuppliers(res.data.suppliers || []))
            .catch(() => setSuppliers([]));
    }, [dispatch]);

    const statusOptions = useMemo(() => ([
        { name: 'New Request', id: 'new' },
        { name: 'Approved Request', id: 'approved' },
        { name: 'PO Made', id: 'poMade' },
        { name: 'Order Sent', id: 'ordered' },
    ]), []);

    const periodLabel = (period, unit) => (unit === 'year' ? period : monthLabel(period));
    const combinedTrendData = useMemo(() => {
        const key = trendPeriod === 'month' ? 'monthly' : 'yearly';
        const requests = summary?.requestsTrend?.[key] || [];
        const inventory = summary?.inventoryTrend?.[key] || [];
        const inventoryMap = new Map(inventory.map((r) => [r.period, r.count]));
        return requests.map((r) => ({
            period: r.period,
            label: periodLabel(r.period, trendPeriod),
            requests: r.count,
            inventory: inventoryMap.get(r.period) || 0,
        }));
    }, [summary, trendPeriod]);

    const expenditureData = useMemo(() => {
        const key = expenditurePeriod === 'month' ? 'monthly' : 'yearly';
        const requestSpend = summary?.requestSpendTrend?.[key] || [];
        const inventorySpend = summary?.inventoryExpenseTrend?.[key] || [];
        const inventoryMap = new Map(inventorySpend.map((r) => [r.period, r.amount]));
        return requestSpend.map((r) => ({
            period: r.period,
            label: periodLabel(r.period, expenditurePeriod),
            requestAmount: r.amount,
            inventoryAmount: inventoryMap.get(r.period) || 0,
            total: r.amount + (inventoryMap.get(r.period) || 0),
        }));
    }, [summary, expenditurePeriod]);

    const totalExpenditureThisPeriod = useMemo(
        () => expenditureData.reduce((sum, d) => sum + d.total, 0),
        [expenditureData],
    );

    return (
        <div className="w-full bg-gray-50 pb-10">
            <div className="mx-auto w-full max-w-[1600px] px-3 py-4 sm:px-4 lg:px-6">
                {/* ---------------------------- Header ---------------------------- */}
                <div className="flex items-center gap-2.5 border-b border-gray-200 pb-4">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-900 text-white">
                        <LuLayoutDashboard size={16} />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-[15px] font-semibold text-gray-900">Dashboard</h1>
                        <p className="truncate text-[12px] text-gray-500">
                            Welcome back{user?.name ? `, ${user.name}` : ''} — here's what's happening.
                        </p>
                    </div>
                </div>

                {/* ---------------------------- KPI cards ---------------------------- */}
                <div className="my-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                    <StatCard label="Total Requests" value={summary?.totalRequests ?? '—'} icon={LuClipboardList} accent="bg-brand-gradient" iconBg="bg-brand-50 text-brand-600" />
                    <StatCard label="Pending Approval" value={summary?.pendingApproval ?? '—'} icon={LuClock} accent="bg-blue-500" iconBg="bg-blue-50 text-blue-600" />
                    <StatCard label="Inventory Items" value={summary?.totalInventoryItems ?? '—'} icon={LuBoxes} accent="bg-teal-500" iconBg="bg-teal-50 text-teal-600" />
                    <StatCard label="Due to Expire" value={summary?.numDueToExpire ?? '—'} icon={LuTriangleAlert} accent="bg-amber-500" iconBg="bg-amber-50 text-amber-600" />
                    <StatCard label="Expired" value={summary?.numExpired ?? '—'} icon={LuTriangleAlert} accent="bg-rose-500" iconBg="bg-rose-50 text-rose-600" />
                    <StatCard label="Delayed Orders" value={summary?.delayedOrders ?? '—'} icon={LuTruck} accent="bg-orange-500" iconBg="bg-orange-50 text-orange-600" />
                </div>

                {/* ---------------------------- Spend KPIs ---------------------------- */}
                {user?.admin === true && (
                <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <StatCard
                        label="Pending Amount"
                        value={summary ? money(summary.pendingAmount) : '—'}
                        icon={LuHourglass}
                        accent="bg-amber-500"
                        iconBg="bg-amber-50 text-amber-600"
                    />
                    <StatCard
                        label="Cleared Amount"
                        value={summary ? money(summary.clearedAmount) : '—'}
                        icon={LuCircleDollarSign}
                        accent="bg-emerald-500"
                        iconBg="bg-emerald-50 text-emerald-600"
                    />
                    <StatCard
                        label="Total Pipeline Value"
                        value={summary ? money(summary.pendingAmount + summary.clearedAmount) : '—'}
                        icon={LuWallet}
                        accent="bg-indigo-500"
                        iconBg="bg-indigo-50 text-indigo-600"
                    />
                </div>
                )}

                {/* ---------------------------- Charts row 1 ---------------------------- */}
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  {user?.admin === true && (
                    <ChartCard
                        title="Requests by Status"
                        subtitle="Current distribution across the full pipeline"
                        empty={!summaryLoading && (summary?.requestsByStatus || []).length === 0}
                    >
                        <ResponsiveContainer width="100%" height={240}>
                            <PieChart>
                                <Pie
                                    data={summary?.requestsByStatus || []}
                                    dataKey="count"
                                    nameKey="label"
                                    innerRadius={55}
                                    outerRadius={85}
                                    paddingAngle={2}
                                >
                                    {(summary?.requestsByStatus || []).map((entry) => (
                                        <Cell key={entry.status} fill={STATUS_COLORS[entry.status] || '#94a3b8'} />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                  )}

                  {user?.admin === true && (
                    <div className="rounded-lg border border-gray-200 bg-white p-4">
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                            <div>
                                <h3 className="text-[13px] font-semibold text-gray-900">
                                    Requests &amp; Inventory Trend
                                </h3>
                                <p className="text-[11px] text-gray-400">
                                    New requests created vs. inventory items added
                                </p>
                            </div>
                            <PeriodToggle value={trendPeriod} onChange={setTrendPeriod} />
                        </div>
                        {!summaryLoading && combinedTrendData.every((d) => d.requests === 0 && d.inventory === 0) ? (
                            <div className="flex h-[240px] items-center justify-center text-[12px] text-gray-400">
                                Not enough data yet.
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={240}>
                                <LineChart data={combinedTrendData} margin={{ left: -20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                                    <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    <Line type="monotone" dataKey="requests" name="Requests" stroke="#4f46e5" strokeWidth={2.5} dot={{ r: 3 }} />
                                    <Line type="monotone" dataKey="inventory" name="Inventory Added" stroke="#0ea5e9" strokeWidth={2.5} dot={{ r: 3 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                  )}
                </div>

                {/* ---------------------------- Charts row 2 ---------------------------- */}
                {user?.admin === true && (
                <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                    <ChartCard
                        title="Inventory by Type"
                        subtitle="Top item types currently in stock"
                        empty={!summaryLoading && (summary?.inventoryByType || []).length === 0}
                    >
                        <ResponsiveContainer width="100%" height={240}>
                            <BarChart data={summary?.inventoryByType || []} margin={{ left: -20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                                <XAxis dataKey="type" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} interval={0} angle={-20} textAnchor="end" height={50} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                    {(summary?.inventoryByType || []).map((entry, i) => (
                                        <Cell key={entry.type} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    {user?.admin === true && (
                    <ChartCard
                        title="Top Suppliers"
                        subtitle="By number of requests placed"
                        empty={!summaryLoading && (summary?.topSuppliers || []).length === 0}
                    >
                        <ResponsiveContainer width="100%" height={240}>
                            <BarChart
                                data={summary?.topSuppliers || []}
                                layout="vertical"
                                margin={{ left: 10 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#eee" horizontal={false} />
                                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                                <YAxis
                                    type="category"
                                    dataKey="supplier"
                                    tick={{ fontSize: 11 }}
                                    axisLine={false}
                                    tickLine={false}
                                    width={110}
                                />
                                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                                <Bar dataKey="count" radius={[0, 4, 4, 0]} fill="#0ea5e9" />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                    )}
                </div>
                )}

                {/* ---------------------------- Total Expenditure ---------------------------- */}
                {user?.admin === true && (
                <div className="mt-3 rounded-lg border border-gray-200 bg-white p-4">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <div>
                            <h3 className="text-[13px] font-semibold text-gray-900">Total Expenditure</h3>
                            <p className="text-[11px] text-gray-400">
                                Requests (ordered) + Inventory (received) combined · {money(totalExpenditureThisPeriod)} shown for this range
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="flex flex-wrap gap-3 text-[11px]">
                                <span className="flex items-center gap-1.5 text-gray-500">
                                    <span className="h-2 w-2 rounded-full bg-indigo-500" /> Requests
                                </span>
                                <span className="flex items-center gap-1.5 text-gray-500">
                                    <span className="h-2 w-2 rounded-full bg-teal-500" /> Inventory
                                </span>
                            </div>
                            <PeriodToggle value={expenditurePeriod} onChange={setExpenditurePeriod} />
                        </div>
                    </div>
                    {!summaryLoading && expenditureData.every((d) => d.total === 0) ? (
                        <div className="flex h-[240px] items-center justify-center text-[12px] text-gray-400">
                            No expenditure in this period.
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height={240}>
                            <BarChart data={expenditureData} margin={{ left: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                                <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                                <YAxis
                                    tick={{ fontSize: 11 }}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(v) => (v >= 1000 ? `₹${(v / 1000).toFixed(0)}k` : `₹${v}`)}
                                />
                                <Tooltip formatter={(v) => money(v)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="requestAmount" name="Requests" stackId="a" fill="#4f46e5" />
                                <Bar dataKey="inventoryAmount" name="Inventory" stackId="a" fill="#14b8a6" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
                )}

                {/* ---------------------------- Tables ---------------------------- */}
                <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                    <PendingApprovalTable items={pendingApproval} loading={tablesLoading} />
                    <DelayedOrdersTable items={delayedOrders} loading={tablesLoading} />
                </div>

                {/* ---------------------------- Recent inventory ---------------------------- */}
                <div className="mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white">
                    <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
                        <div className="flex items-center gap-2">
                            <LuPackage size={15} className="text-gray-400" />
                            <h3 className="text-[13px] font-semibold text-gray-900">Recently Added Inventory</h3>
                        </div>
                        <button
                            type="button"
                            onClick={() => navigate(INVENTORY)}
                            className="text-[11.5px] font-medium text-gray-500 hover:text-gray-800"
                        >
                            View all
                        </button>
                    </div>
                    {(!summary?.recentInventory || summary.recentInventory.length === 0) ? (
                        <p className="px-4 py-8 text-center text-[12.5px] text-gray-400">No inventory items yet.</p>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {summary.recentInventory.slice(0, 5).map((row) => (
                                <button
                                    type="button"
                                    key={row.id}
                                    onClick={() => navigate(`${INVENTORY}?item=${encodeURIComponent(row.item)}&highlight=${row.id}`)}
                                    title="Open in Inventory"
                                    className="group flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left transition-colors hover:bg-gray-50"
                                >
                                    <div className="min-w-0">
                                        <p className="truncate text-[12.5px] font-medium text-gray-800 group-hover:text-brand-700">{row.item}</p>
                                        <p className="truncate text-[11px] text-gray-400">
                                            {row.catalog} · Qty {row.quantity}
                                        </p>
                                    </div>
                                    <div className="flex shrink-0 items-center gap-2">
                                        <div className="text-right">
                                            <p className="text-[11px] text-gray-400">
                                                Added {new Date(row.addDate).toLocaleDateString()}
                                            </p>
                                            <p className="text-[11px] text-gray-400">
                                                Expires {new Date(row.expireDate).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <LuChevronRight size={13} className="text-gray-300 group-hover:text-gray-600" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* ---------------------------- Quick links ---------------------------- */}
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    <ChipCloud
                        title="Browse Inventory by Type"
                        icon={LuBoxes}
                        items={inventoryTypes}
                        hrefBuilder={(t) => `inventory?type=${encodeURIComponent(t)}`}
                    />
                    <ChipCloud
                        title="Requests by Requester"
                        icon={LuUsers}
                        items={userNames}
                        hrefBuilder={(u) => `requests?user=${encodeURIComponent(u)}`}
                    />
                    <ChipCloud
                        title="Requests by Supplier"
                        icon={LuBuilding2}
                        items={suppliers}
                        getLabel={(s) => s.name}
                        getKey={(s) => s.email || s.name}
                        hrefBuilder={(s) => `requests?supplier=${encodeURIComponent(s.name)}`}
                    />
                    <ChipCloud
                        title="Requests by Status"
                        icon={LuClipboardList}
                        items={statusOptions}
                        getLabel={(s) => s.name}
                        getKey={(s) => s.id}
                        hrefBuilder={(s) => `requests?status=${encodeURIComponent(s.id)}`}
                    />
                </div>
            </div>
        </div>
    );
}